import {
  Download,
  Eye,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { DragEvent, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { formatFileSize } from '@/components/documents-v2/utils/fileUtils';
import { getFileIcon } from '@/components/documents-v2/utils/fileIcons';
import { cn } from '@/lib/utils';
import {
  useDestroyBlobMutation,
  useGetBlobsQuery,
} from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import type { Blob as ApiBlob } from '@/services/types/blob-types';

import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

interface DocumentsPanelProps {
  matterId: string;
}

export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({ matterId }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewBlob, setPreviewBlob] = useState<ApiBlob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch blobs and collection tree
  const {
    data: blobs,
    isLoading: isLoadingBlobs,
    refetch: refetchBlobs,
  } = useGetBlobsQuery(matterId);
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId);
  const rootCollectionId = collectionTree?.id;

  // Upload manager
  const { uploadProgress, handleUploadToCollection, clearUploads } =
    useUploadManagerUnified({
      matterId,
      refetch: refetchBlobs,
    });

  // Delete mutation
  const [destroyBlob, { isLoading: isDeleting }] = useDestroyBlobMutation();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!rootCollectionId || files.length === 0) return;
      await handleUploadToCollection(files, rootCollectionId, false, false);
    },
    [rootCollectionId, handleUploadToCollection],
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if leaving the container
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await uploadFiles(files);
      }
    },
    [uploadFiles],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        await uploadFiles(files);
      }
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadFiles],
  );

  const handleDelete = useCallback(
    async (blob: ApiBlob) => {
      try {
        await destroyBlob({ matterId, blobId: blob.id }).unwrap();
        toast.success(`Deleted ${blob.name}`);
      } catch {
        toast.error('Failed to delete file');
      }
    },
    [matterId, destroyBlob],
  );

  const handleDownload = useCallback((blob: ApiBlob) => {
    window.open(blob.download_url, '_blank');
  }, []);

  const isPreviewable = useCallback((blob: ApiBlob) => {
    const ext = blob.file_extension?.toLowerCase();
    const mime = blob.file_mime_type?.toLowerCase();
    if (mime?.startsWith('image/')) return true;
    if (ext === 'pdf' || mime === 'application/pdf') return true;
    return false;
  }, []);

  const activeUploads = uploadProgress.filter(
    (u) => u.status === 'uploading' || u.status === 'pending',
  );
  const hasActiveUploads = activeUploads.length > 0;

  // Preview modal
  if (previewBlob) {
    const isPdf =
      previewBlob.file_extension?.toLowerCase() === 'pdf' ||
      previewBlob.file_mime_type?.toLowerCase() === 'application/pdf';
    const isImage = previewBlob.file_mime_type?.toLowerCase()?.startsWith('image/');

    return (
      <div className="flex flex-col h-full">
        {/* Preview header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-slate-50">
          <span className="text-xs font-semibold text-gray-900 truncate flex-1 mr-2">
            {previewBlob.name}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleDownload(previewBlob)}
            >
              <Download className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setPreviewBlob(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 min-h-0 overflow-hidden bg-gray-100">
          {isPdf && (
            <iframe
              src={`${previewBlob.pdf_preview_url || previewBlob.download_url}#toolbar=0`}
              className="w-full h-full border-0"
              title={previewBlob.name}
            />
          )}
          {isImage && (
            <div className="flex items-center justify-center h-full p-4">
              <img
                src={previewBlob.download_url}
                alt={previewBlob.name}
                className="max-w-full max-h-full object-contain rounded"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-30 bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <Upload className="h-6 w-6 text-blue-600" />
          </div>
          <p className="text-sm font-semibold text-blue-700">Drop files here</p>
          <p className="text-xs font-medium text-blue-500 mt-1">
            Files will be uploaded to this matter
          </p>
        </div>
      )}

      {/* Upload progress section */}
      {hasActiveUploads && (
        <div className="px-3 py-2 border-b border-gray-200 bg-blue-50/50 space-y-2">
          {activeUploads.map((upload, i) => (
            <div key={i} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700 truncate flex-1 mr-2">
                  {upload.fileName}
                </span>
                <span className="text-[10px] font-medium text-blue-600">
                  {Math.round(upload.progress)}%
                </span>
              </div>
              <Progress value={upload.progress} className="h-1 bg-blue-100" />
            </div>
          ))}
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingBlobs ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin mb-2" />
            <p className="text-xs font-medium text-gray-500">Loading files...</p>
          </div>
        ) : !blobs || blobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FileText className="h-5 w-5 text-slate-500" />
            </div>
            <p className="text-xs font-semibold text-gray-900 mb-1">
              No documents yet
            </p>
            <p className="text-[10px] font-medium text-gray-500 text-center mb-4">
              Drag & drop files anywhere on this panel, or click below to upload.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[10px] gap-1.5"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-3 w-3" />
              Choose Files
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {blobs.map((blob) => (
              <div
                key={blob.id}
                className="group flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
              >
                {/* File icon */}
                <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                  {getFileIcon(blob.file_extension, 'sm')}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {blob.name}
                  </p>
                  <p className="text-[10px] font-medium text-gray-500">
                    {formatFileSize(blob.file_size)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {isPreviewable(blob) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setPreviewBlob(blob)}
                    >
                      <Eye className="h-3 w-3 text-gray-500" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleDownload(blob)}
                  >
                    <Download className="h-3 w-3 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:text-red-600"
                    onClick={() => handleDelete(blob)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3 w-3 text-gray-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom upload bar - always visible when files exist */}
      {blobs && blobs.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gradient-to-r from-slate-50 to-blue-50">
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-[10px] gap-1.5 border-dashed"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3 w-3" />
            Upload More Files
          </Button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};
